<script setup>
import { Head, Link, usePage } from '@inertiajs/vue3';
import { computed } from 'vue';
import PriceCompareLayout from '@/Layouts/PriceCompareLayout.vue';

const page = usePage();
const seo = page.props.seo;
const menuItems = page.props.menuItems;

/*
 * Virksomhedens oplysninger.
 *
 * De har ligget i konfigurationen hele tiden og blev kun brugt i
 * Organization-schemaet på forsiden. En bruger der vil vide hvem der står bag
 * tallene, kunne ikke finde det nogen steder.
 */
const virksomhed = computed(() => page.props.virksomhed || {});
</script>

<template>
    <PriceCompareLayout :menu-items="menuItems">
        <Head :title="seo?.title ?? 'Om os - PriceCompare'" />
        <div class="container py-5">
            <div class="row justify-content-center">
                <div class="col-md-8">
                    <div class="card border-0 shadow-sm mb-4 bg-white">
                        <div class="card-body py-3 px-4 text-start">
                            <h1 class="h2 mb-0">Om os</h1>
                        </div>
                    </div>
                    <div class="card border-0 shadow-sm">
                        <div class="card-body p-4">
                            <!--
                                Om-siden er ikke pynt.

                                For et site der siger "her er den billigste pris", er
                                afsenderens troværdighed hele produktet. Google behandler
                                det som et YMYL-lignende område, fordi forbrugere bruger
                                penge ud fra vores tal, og sprogmodeller vægter kilder der
                                kan identificeres. Siden havde tre afsnit uden ét faktum:
                                ingen CVR, intet om hvor priserne kommer fra, og intet om
                                hvordan sitet tjener penge.
                            -->
                            <p>
                                PriceCompare sammenligner priser på elektronik og hardware fra danske
                                webshops. Formålet er at gøre det hurtigt at se hvad en vare koster
                                hos hver butik, og hvad den har kostet før, så du kan afgøre om det
                                er nu du skal købe.
                            </p>

                            <h2 class="h5 mt-4">Hvor kommer priserne fra</h2>
                            <p>
                                Priserne hentes direkte fra butikkernes egne produktfeeds, altså de
                                datafiler butikkerne selv udstiller. Feedene hentes flere gange i
                                døgnet, og hver ændring gemmes, så prishistorikken på en vare er
                                målte tal og ikke et skøn. Kataloget rummer i dag godt 138.000 varer
                                og over 10 millioner registrerede prisændringer.
                            </p>
                            <p>
                                Vi handler ikke selv, og vi lægger ikke noget oveni prisen. Står der
                                en pris her, er det den butikken selv oplyser. Ser du en pris der
                                ikke passer, må du meget gerne skrive, for så er der noget galt i
                                feedet eller i vores behandling af det.
                            </p>

                            <h2 class="h5 mt-4">Hvordan tjener sitet penge</h2>
                            <p>
                                Når du klikker videre til en butik og handler, får vi provision.
                                Det ændrer ikke din pris, og det påvirker ikke rækkefølgen:
                                listerne er sorteret efter laveste pris inklusive fragt, og der
                                findes ingen betalt placering. Butikker uden en aftale vises på
                                præcis samme vilkår som dem med.
                            </p>

                            <h2 class="h5 mt-4">Hvem står bag</h2>
                            <p>
                                PriceCompare drives af <strong>{{ virksomhed.navn }}</strong><span v-if="virksomhed.cvr">, CVR {{ virksomhed.cvr }}</span>.
                                <span v-if="virksomhed.adresse">{{ virksomhed.adresse }}.</span>
                                Spørgsmål, rettelser og henvendelser fra butikker kan sendes via
                                <Link href="/kontakt">kontaktsiden</Link><span v-if="virksomhed.email"> eller til {{ virksomhed.email }}</span>.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </PriceCompareLayout>
</template>

<style>
.container {
    max-width: 960px;
}
</style>